/** Shell: the kit AppShell around every screen. Sidebar (jobs, People tree, queues, account), top bar (trail, freshness, point and comment, the bell), the screen; and the things that float over it: the person sheet, the palette, Feedback, the picker, Help and toasts. Signed out, none of it: only the way back in. */
import { Suspense, useEffect, useState, type MouseEvent } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getMe, logout } from "../../api/client";
import { getTenantInfo } from "../../api/tenantInfo";
import { invalidate, useQuery } from "../data/useQuery";
import { useQueues } from "../data/useQueues";
import { AppShell, SkipLink, ToastProvider, useInertBehindModals } from "../kit";
import { PaletteHost } from "../palette";
import { PersonPanelHost } from "../panel";
import { pageTitle } from "./crumbs";
import { Feedback } from "./Feedback";
import { Help } from "./Help";
import { routeOf } from "./links";
import { Nav } from "./Nav";
import { PickHost } from "./pick";
import { usePick } from "./pick/shortcut";
import { preloadScreens } from "./screens";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { SignedOut } from "./SignedOut";
import { Top } from "./Top";
import { warmNow, warmOnRest } from "./warm";

type Floating = "palette" | "feedback" | "help" | null;

const MAIN = "main";

export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [floating, setFloating] = useState<Floating>(null);
  const me = useQuery("v2:me", getMe);
  const signedOut = me.data === null;
  const queues = useQueues(!signedOut);
  const tenant = useQuery("v2:tenant", getTenantInfo);
  const close = () => setFloating(null);
  const pick = usePick();
  useInertBehindModals();

  // ⌘K / Ctrl+K opens (and closes) the palette from anywhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setFloating((now) => (now === "palette" ? null : "palette"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.title = pageTitle(pathname);
  }, [pathname]);

  useEffect(() => {
    const timer = window.setTimeout(preloadScreens, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  /**
   * Kit links are plain anchors: a plain click on one becomes a router navigation. Another screen crossfades in
   * (a view transition, where the browser has them, #714); a change within a screen swaps its own content in place.
   */
  const onClick = (event: MouseEvent) => {
    const route = routeOf(event);
    if (route === null) return;
    event.preventDefault();
    const screen = new URL(route, window.location.href).pathname !== pathname;
    void navigate(route, screen ? { viewTransition: true } : undefined);
  };

  /** The session ends on the server; everything on screen then asks again, and the page reads signed out. */
  const signOut = () => void logout().then(() => invalidate());

  // Nothing of the association stays on screen once the session has ended: no queues, no freshness, no search.
  if (signedOut) return <SignedOut />;

  const nav = (
    <Nav queues={queues} me={me.data} capabilities={tenant.data?.capabilities} onPalette={() => setFloating("palette")} onFeedback={() => setFloating("feedback")} onHelp={() => setFloating("help")} onSignOut={signOut} />
  );

  return (
    <ToastProvider>
      {/* Listens for clicks on the links inside it; the links are the controls. A link the pointer rests on, or focus comes to, is warmed (`warm.ts`). A closed drawer casts no shadow onto the page's edge (kit-requests.md, AppShell). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="contents [&_[aria-modal][inert]]:shadow-none" onClick={onClick} onPointerOver={(event) => warmOnRest(event.target)} onFocus={(event) => warmNow(event.target)}>
        <SkipLink targetId={MAIN}>Skip to content</SkipLink>
        <AppShell sidebar={nav} topBar={<Top queues={queues} onPick={pick.start} />}>
          <div id={MAIN} tabIndex={-1} className="flex w-full flex-col outline-none">
            <Suspense fallback={<ScreenSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
          <PersonPanelHost />
          <PaletteHost open={floating === "palette"} onClose={close} onFeedback={() => setFloating("feedback")} onHelp={() => setFloating("help")} />
          <Feedback open={floating === "feedback"} onClose={close} />
          <PickHost open={pick.open} onClose={pick.stop} />
          <Help open={floating === "help"} onClose={close} />
        </AppShell>
      </div>
    </ToastProvider>
  );
}
